import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useGetDncLookupPricesQuery, useLookUpDncNumbersMutation } from "@/store/rest_api";
import { DncCheckMode } from "@/enum/DncCheckMode";
import { DncListStatus } from "@/enum/DncListStatus";
import { DncLookupResult } from "@/model/interface/Compliance";
import { apiErrorMessage } from "@/util/apiError";

interface DncCheckPanelProps {
  phoneNumbers: string[];
}

export const DNC_STATUS_LABELS: Record<DncListStatus, string> = {
  [DncListStatus.CLEAN]: "Not on a Do Not Call list",
  [DncListStatus.STATE_LISTED]: "On a state Do Not Call list, calls and texts blocked",
  [DncListStatus.NATIONAL_LISTED]: "On the national Do Not Call list, calls and texts blocked",
  [DncListStatus.LITIGATOR]: "Known litigator, calls and texts blocked",
};

export default function DncCheckPanel({ phoneNumbers }: DncCheckPanelProps) {
  const [results, setResults] = useState<DncLookupResult[] | null>(null);
  const { data: prices } = useGetDncLookupPricesQuery();
  const [lookUpNumbers, { isLoading }] = useLookUpDncNumbersMutation();
  const numbers = phoneNumbers.filter((phoneNumber) => phoneNumber.trim().length > 0);

  const formatPrice = (price: number) => `$${(price * numbers.length).toFixed(2)}`;

  const handleCheck = async (mode: DncCheckMode) => {
    try {
      const response = await lookUpNumbers({ phoneNumbers: numbers, mode }).unwrap();
      setResults(response.results);
      if (response.queuedCount > 0) {
        toast.success("Checking. We will notify you when the results are in.");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not check the Do Not Call lists"));
    }
  };

  if (results) {
    return (
      <ul className="space-y-2 text-sm">
        {results.map((result) => (
          <li key={result.phoneNumberE164} className="flex justify-between gap-4">
            <span className="font-medium tabular-nums">{result.phoneNumberE164}</span>
            <span className="text-right text-muted-foreground">
              {result.listStatus ? DNC_STATUS_LABELS[result.listStatus] : "Checking, we will notify you"}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  if (prices && !prices.available) {
    return <p className="text-sm text-muted-foreground">Do Not Call checks are not available right now.</p>;
  }

  return (
    <div className="grid gap-2">
      <Button disabled={!prices || isLoading || numbers.length === 0} onClick={() => void handleCheck(DncCheckMode.INSTANT)}>
        Check instantly{prices ? ` (up to ${formatPrice(prices.instantPrice)})` : ""}
      </Button>
      <Button
        variant="outline"
        disabled={!prices || isLoading || numbers.length === 0}
        onClick={() => void handleCheck(DncCheckMode.DELAYED)}
      >
        Check in a few minutes{prices ? ` (up to ${formatPrice(prices.delayedPrice)})` : ""}
      </Button>
    </div>
  );
}
